/* La Sala — motore delle sezioni cinematiche
   Richiede gsap.min.js, scrolltrigger.min.js e (facoltativo) lenis.min.js */

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ready = () => typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';

  // Mobile: meno pixel da riempire, meno punti da campionare. Il telefono è il caso
  // che conta di più e il canvas a piena risoluzione lo strozza.
  const isSmall = () => window.innerWidth < 768;
  const isTouch = () => window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  /* ---------------------------------------------------------------- scorrimento fluido */

  /* ------------------------------------------------- altezze della testa di pagina

     Horizon pubblica --header-height e --header-group-height, ma a pagina ferma
     valgono ENTRAMBE l'altezza del solo header: le corregge solo dopo il primo
     scorrimento. Chi ci costruiva sopra si ritrovava il layout che saltava proprio
     mentre si scorreva. Le misuriamo noi dagli elementi, che non mentono mai. */

  function misuraTesta() {
    const gruppi = [...document.querySelectorAll('.shopify-section-group-header-group')];
    if (!gruppi.length) return;
    const sticky = gruppi[gruppi.length - 1].offsetHeight;      // l'header che resta in alto
    const sopra = gruppi.slice(0, -1)                            // barra annuncio & co.
      .reduce((somma, el) => somma + el.offsetHeight, 0);
    const root = document.documentElement.style;
    root.setProperty('--ls-header', sticky + 'px');
    root.setProperty('--ls-sopra', sopra + 'px');
  }

  /* Horizon non fa scorrere il documento: scorre un contenitore interno (.page-wrapper),
     alto quanto il viewport con overflow-y auto. Senza individuarlo, ScrollTrigger misura
     sempre zero e nessuna animazione legata allo scorrimento parte. */
  function findScroller() {
    const el = document.querySelector('.page-wrapper');
    if (el) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') return el;
    }
    return window;
  }

  let smoothStarted = false;
  function startSmoothScroll(scroller) {
    // niente smooth scroll nell'editor del tema: interferisce con l'anteprima
    if (smoothStarted || reduced || typeof Lenis === 'undefined') return;
    if (window.Shopify && window.Shopify.designMode) return;
    // su telefono lo scorrimento nativo ha già il suo slancio ed è più fluido:
    // interporre Lenis aggiunge lavoro e su iOS litiga con la barra che si ritrae
    if (isTouch()) return;

    // ⚠️ Con il contenitore di Horizon, Lenis misura come "contenuto" il wrapper stesso —
    // alto quanto il viewport — e ne ricava un limite di scorrimento pari a zero: a quel
    // punto intercetta la rotella e la pagina non scorre più. Lo usiamo solo quando a
    // scorrere è la finestra, dove il calcolo è corretto.
    if (scroller !== window) return;
    smoothStarted = true;

    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    window.__lasalaLenis = lenis;
  }

  /* ---------------------------------------------------------------- colate d'olio

     L'olio non sale dal basso: cola dall'alto. Ogni rivolo parte con un suo ritardo
     e scende a una sua velocità, con la punta arrotondata e una goccia che ogni tanto
     si stacca e corre giù per conto suo. */

  class OilDrips {
    constructor(canvas) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d', { alpha: true });
      this.p = 0;          // 0 = niente, 1 = colate a fondo schermo
      this.t = 0;
      this.running = false;
      this.resize();
      this._resize = () => { this.resize(); this.seed(); };
      window.addEventListener('resize', this._resize, { passive: true });
      this.seed();
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, isSmall() ? 1.5 : 2);
      const r = this.canvas.getBoundingClientRect();
      this.w = Math.max(1, r.width);
      this.h = Math.max(1, r.height);
      this.canvas.width = Math.round(this.w * dpr);
      this.canvas.height = Math.round(this.h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    seed() {
      const n = isSmall() ? 9 : 20;
      const rnd = (a, b) => a + Math.random() * (b - a);
      this.drips = [];
      for (let i = 0; i < n; i++) {
        const slot = (i + rnd(-0.38, 0.38)) / n;
        const sottile = Math.random() < 0.45;
        this.drips.push({
          x: slot * this.w,
          w: sottile ? rnd(3, isSmall() ? 8 : 11) : rnd(isSmall() ? 9 : 13, isSmall() ? 20 : 34),
          delay: rnd(0, 0.5),
          speed: rnd(0.9, 2.2),
          // metà dei rivoli si esaurisce prima del fondo: è quello che rende la
          // colata credibile invece che una tenda di stalattiti tutte uguali
          maxLen: Math.random() < 0.5 ? rnd(0.28, 0.72) : rnd(0.9, 1.25),
          sway: rnd(4, 14),
          phase: rnd(0, Math.PI * 2),
          bead: rnd(0.35, 0.95),
        });
      }
      // la fascia in cima, da cui tutto cola
      this.lip = isSmall() ? 26 : 40;
    }

    gradiente(y0, y1) {
      const g = this.ctx.createLinearGradient(0, y0, 0, y1);
      g.addColorStop(0, '#f6d98a');
      g.addColorStop(0.12, '#e8b33a');
      g.addColorStop(0.55, '#c2881a');
      g.addColorStop(1, '#7c4f0c');
      return g;
    }

    draw() {
      const { ctx, w, h } = this;
      ctx.clearRect(0, 0, w, h);
      if (this.p <= 0.001) return;

      // labbro superiore: cresce appena, come olio che trabocca
      const lipH = this.lip * Math.min(1, this.p * 4);
      ctx.fillStyle = this.gradiente(0, lipH + 34);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(w, 0);
      const passo = Math.max(8, w / 90);
      for (let x = w; x >= 0; x -= passo) {
        const y =
          lipH +
          Math.sin(x * 0.011 + this.t * 0.5) * 6 +
          Math.sin(x * 0.027 - this.t * 0.8) * 3.5;
        ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();

      for (const d of this.drips) {
        const adv = (this.p - d.delay) * d.speed;
        if (adv <= 0) continue;
        const len = Math.min(d.maxLen, adv) * (h + 140);
        const xw = Math.sin(this.t * 0.6 + d.phase) * d.sway * (1 - Math.min(1, adv / d.maxLen));
        const x = d.x + xw;
        const top = lipH - 6;
        const bot = top + len;
        const half = d.w / 2;

        ctx.fillStyle = this.gradiente(top, Math.min(bot, h));

        // il rivolo si assottiglia verso la punta
        ctx.beginPath();
        ctx.moveTo(x - half, top);
        ctx.lineTo(x + half, top);
        ctx.quadraticCurveTo(x + half * 0.92, bot - half * 1.6, x + half * 0.55, bot - half * 0.7);
        ctx.arc(x, bot - half * 0.7, half * 0.62, 0, Math.PI);
        ctx.quadraticCurveTo(x - half * 0.92, bot - half * 1.6, x - half, top);
        ctx.closePath();
        ctx.fill();

        // riflesso lungo il rivolo
        ctx.fillStyle = 'rgba(255, 244, 205, 0.32)';
        ctx.beginPath();
        ctx.rect(x - half * 0.42, top, Math.max(1, half * 0.3), Math.max(0, len - half));
        ctx.fill();

        // la goccia che si stacca e corre avanti
        if (adv > d.bead && d.maxLen > 0.85) {
          const bp = Math.min(1, (adv - d.bead) * 1.5);
          const by = bot + bp * 190;
          if (by < h + 40) {
            const br = half * (0.78 - bp * 0.2);
            ctx.fillStyle = this.gradiente(by - br, by + br);
            ctx.beginPath();
            ctx.ellipse(x, by, br, br * 1.35, 0, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }

    loop() {
      if (!this.running) return;
      this.t += 0.016;
      this.draw();
      requestAnimationFrame(() => this.loop());
    }

    start() { if (!this.running) { this.running = true; this.loop(); } }
    stop() { this.running = false; }
    destroy() { this.stop(); window.removeEventListener('resize', this._resize); }
  }

  /* --------------------------------------------------------------------------- l'hero */

  const instances = new WeakMap();

  function buildHero(root) {
    if (instances.has(root)) return;

    const canvas = root.querySelector('[data-oil-canvas]');
    const photo  = root.querySelector('.oil-hero__photo-img');
    let video    = root.querySelector('[data-oil-video]');
    const words  = root.querySelectorAll('.oil-hero__word');
    const extras = root.querySelectorAll('[data-oil-fade]');
    const titles = root.querySelector('.oil-hero__titles');
    const inst   = { triggers: [], oil: null };
    instances.set(root, inst);

    if (reduced) {
      gsap.set([words, extras], { clearProps: 'all' });
      return;
    }

    if (words.length) {
      inst.triggers.push(
        gsap.timeline({ scrollTrigger: { trigger: root, start: 'top 85%', once: true } })
          .to(words, { y: 0, opacity: 1, duration: 1.15, ease: 'power3.out', stagger: 0.075 })
          .to(extras, { opacity: 1, duration: 0.8, ease: 'power2.out', stagger: 0.12 }, '-=0.6')
          .scrollTrigger
      );
    }

    // Con l'olio vero in scena — foto o filmato — le colate disegnate sono di
    // troppo: restano solo come ripiego quando non c'è né l'una né l'altro.
    if (canvas && !video && !photo) {
      const oil = new OilDrips(canvas);
      inst.oil = oil;

      const st = ScrollTrigger.create({
        trigger: root,
        start: 'top top',
        end: 'bottom bottom',
        onEnter: () => oil.start(),
        onEnterBack: () => oil.start(),
        onLeave: () => oil.stop(),
        onLeaveBack: () => { oil.stop(); oil.p = 0; oil.draw(); },
        onUpdate: (self) => {
          oil.p = gsap.utils.clamp(0, 1, self.progress / 0.82);
          // più l'olio scende, più l'oro del titolo si accende
          if (titles) titles.style.setProperty('--gold-lit', oil.p.toFixed(3));
          if (video && video.duration && !video.__noSeek) {
            try { video.currentTime = video.duration * self.progress; } catch (e) { video.__noSeek = true; }
          }
        },
      });
      inst.triggers.push(st);

    }

    if (photo) {
      inst.triggers.push(
        gsap.fromTo(photo,
          { yPercent: -5, scale: 1.12 },
          {
            yPercent: 6, scale: 1.02, ease: 'none',
            scrollTrigger: { trigger: root, start: 'top top', end: 'bottom bottom', scrub: 0.5 },
          }
        ).scrollTrigger
      );
      inst.triggers.push(
        ScrollTrigger.create({
          trigger: root,
          start: 'top top',
          end: 'bottom bottom',
          onUpdate: (self) => {
            if (titles) {
              titles.style.setProperty('--gold-lit', gsap.utils.clamp(0, 1, self.progress / 0.7).toFixed(3));
            }
          },
        })
      );
    }

    // Col filmato è lui a comandare: lo scorrimento lo fa avanzare fotogramma
    // per fotogramma, e l'oro del titolo si accende di pari passo.
    //
    // ⚠️ Gli asset del tema non rispondono alle richieste di intervallo, quindi il
    // browser dichiara il filmato non spostabile (seekable vuoto) e ogni seek resta
    // a zero. Lo tiriamo giù in un colpo solo e lo serviamo dalla memoria: pesa
    // poco più di cento kilobyte, e da lì il seek è immediato e preciso.
    // Su telefono il filmato è orizzontale e lo schermo no: croppato perde il filo
    // d'olio. Meglio la foto verticale del cucchiaio, che è anche più leggera.
    if (video && isSmall()) {
      video.remove();
      video = null;
    }

    if (video) {
      const stVideo = ScrollTrigger.create({
        trigger: root,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self) => {
          if (titles) {
            titles.style.setProperty('--gold-lit', gsap.utils.clamp(0, 1, self.progress / 0.82).toFixed(3));
          }
          if (video.duration && !video.__noSeek) {
            try { video.currentTime = video.duration * self.progress; }
            catch (e) { video.__noSeek = true; }
          }
        },
      });
      inst.triggers.push(stVideo);

      const sorgente = video.querySelector('source');
      if (sorgente && !video.__inline) {
        video.__inline = true;
        fetch(sorgente.src)
          .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
          .then((b) => {
            video.src = URL.createObjectURL(b);
            video.load();
            video.addEventListener('loadeddata', () => {
              // chi è arrivato qui già a metà pagina deve vedere il fotogramma giusto,
              // non il primo: senza questo il filmato resta fermo finché non si scorre
              ScrollTrigger.refresh();
              stVideo.refresh();
              try { video.currentTime = video.duration * stVideo.progress; } catch (e) {}
            }, { once: true });
          })
          .catch(() => { /* resta la foto sotto: la scena funziona comunque */ });
      }
    }

    const hint = root.querySelector('.oil-hero__hint');
    if (hint) {
      inst.triggers.push(
        gsap.to(hint, {
          opacity: 0, ease: 'none',
          scrollTrigger: { trigger: root, start: 'top top', end: '14% top', scrub: 0.4 },
        }).scrollTrigger
      );
    }
  }

  function destroyHero(root) {
    const inst = instances.get(root);
    if (!inst) return;
    inst.triggers.forEach((t) => t && t.kill && t.kill());
    if (inst.oil) inst.oil.destroy();
    instances.delete(root);
  }

  /* ------------------------------------------------- scena: immagine con parallasse */

  function buildReveal(root) {
    if (instances.has(root)) return;
    const inst = { triggers: [], oil: null };
    instances.set(root, inst);

    const img = root.querySelector('[data-reveal-img]');
    const lines = root.querySelectorAll('[data-reveal-line]');

    if (reduced) {
      gsap.set(lines, { opacity: 1, y: 0 });
      return;
    }

    if (lines.length) {
      inst.triggers.push(
        gsap.to(lines, {
          opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', stagger: 0.12,
          scrollTrigger: { trigger: root, start: 'top 72%', once: true },
        }).scrollTrigger
      );
    }

    const motion = root.dataset.motion || 'parallax';

    if (img && motion === 'dolly') {
      // la foto si ingrandisce piano mentre si scende: sembra di entrarci dentro.
      // Su telefono l'ingrandimento è più contenuto, o si perde il soggetto ai bordi.
      const zoom = isSmall() ? 1.26 : 1.4;
      inst.triggers.push(
        gsap.fromTo(img, { scale: 1.02, yPercent: -2 }, {
          scale: zoom, yPercent: 2, ease: 'none',
          scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: 0.4 },
        }).scrollTrigger
      );
    } else if (img && motion !== 'none') {
      // su telefono una parallasse ampia costa scroll senza aggiungere nulla
      const amp = isSmall() ? 4 : 8;
      inst.triggers.push(
        gsap.fromTo(img, { yPercent: -amp }, {
          yPercent: amp, ease: 'none',
          scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true },
        }).scrollTrigger
      );
    }
  }

  /* ---------------------------------------------------- chiusura: numeri e invito */

  function buildClosing(root) {
    if (instances.has(root)) return;
    const inst = { triggers: [], oil: null };
    instances.set(root, inst);

    const lines = root.querySelectorAll('[data-closing-line]');
    if (!lines.length) return;

    if (reduced) { gsap.set(lines, { opacity: 1, y: 0 }); return; }

    inst.triggers.push(
      gsap.to(lines, {
        opacity: 1, y: 0, duration: 0.85, ease: 'power3.out', stagger: 0.09,
        scrollTrigger: { trigger: root, start: 'top 78%', once: true },
      }).scrollTrigger
    );
  }

  /* ------------------------------------------------ garanzia sotto il prodotto */

  function buildAssure(root) {
    if (instances.has(root)) return;
    const inst = { triggers: [], oil: null };
    instances.set(root, inst);
    const lines = root.querySelectorAll('[data-assure-line]');
    if (!lines.length) return;
    if (reduced) { gsap.set(lines, { opacity: 1, y: 0 }); return; }
    inst.triggers.push(
      gsap.to(lines, {
        opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.09,
        scrollTrigger: { trigger: root, start: 'top 80%', once: true },
      }).scrollTrigger
    );
  }

  /* ------------------------------------------------------ pagina di prenotazione */

  function buildBooking(root) {
    if (instances.has(root)) return;
    const inst = { triggers: [], oil: null };
    instances.set(root, inst);

    const lines = root.querySelectorAll('[data-book-line]');
    if (lines.length && !reduced) {
      inst.triggers.push(
        gsap.to(lines, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.08,
          scrollTrigger: { trigger: root, start: 'top 72%', once: true },
        }).scrollTrigger
      );
    } else if (lines.length) {
      gsap.set(lines, { opacity: 1, y: 0 });
    }

    // Prenotazione andata a buon fine: è il momento che conta per le campagne.
    // Il pixel non c'è ancora, ma quando ci sarà questo evento parte da solo.
    if (root.querySelector('[data-book-done]') && !root.__segnalato) {
      root.__segnalato = true;
      try { if (typeof fbq === 'function') fbq('track', 'Lead', { content_name: 'Prenotazione olio' }); } catch (e) {}
      try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'prenotazione_olio' }); } catch (e) {}
    }
  }

  /* --------------------------------------------------------------------------- avvio */

  function init(scope = document) {
    if (!ready()) return;
    gsap.registerPlugin(ScrollTrigger);

    misuraTesta();
    const scroller = findScroller();
    if (scroller !== window) ScrollTrigger.defaults({ scroller });
    startSmoothScroll(scroller);

    scope.querySelectorAll('[data-oil-hero]').forEach(buildHero);
    scope.querySelectorAll('[data-oil-reveal]').forEach(buildReveal);
    scope.querySelectorAll('[data-oil-closing]').forEach(buildClosing);
    scope.querySelectorAll('[data-oil-booking]').forEach(buildBooking);
    scope.querySelectorAll('[data-oil-assure]').forEach(buildAssure);
    ScrollTrigger.refresh();
  }

  window.addEventListener('resize', () => { misuraTesta(); }, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // il theme editor ricarica le sezioni al volo
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-oil-hero],[data-oil-reveal],[data-oil-closing],[data-oil-booking],[data-oil-assure]').forEach(destroyHero);
  });
})();
